import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiMapPin,
  FiBriefcase,
  FiTruck,
  FiArrowUpCircle,
  FiArrowDownCircle,
  FiCalendar
} from 'react-icons/fi';

import Api from '../../Api';
import { hasPermission } from '../../utils/permissions';
import {
  Card,
  PageComponent,
  Skeletons,
  MultiSelect,
  DatePeriodFilter,
  getDateParams
} from '../../components';

import './style.css';

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  
  const [periodMode, setPeriodMode] = useState('monthly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });

  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('authUser'));
    } catch (e) {
      return null;
    }
  });

  const [companies, setCompanies] = useState([]);
  const [filterCompanyIds, setFilterCompanyIds] = useState(['all']);
  
  // Extract Skeleton component from namespace
  const { Skeleton } = Skeletons;

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      if (Array.isArray(data)) {
        setCompanies(data);
      }
    } catch (err) {
      console.error('Erro ao carregar empresas', err);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = filterCompanyIds;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);
    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      setFilterCompanyIds([allValue, ...allIds]);
      return;
    }
    if (!isAllSelected && wasAllSelected) {
      setFilterCompanyIds([]);
      return;
    }
    if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      setFilterCompanyIds(remaining);
      return;
    }
    const specificIds = newValues.filter(v => v !== allValue);
    if (specificIds.length === companies.length && companies.length > 0) {
      setFilterCompanyIds([allValue, ...specificIds]);
    } else {
      setFilterCompanyIds(specificIds);
    }
  }

  function mergeDashboards(list) {
    const result = {
      counts: { employees: 0, serviceStations: 0, customers: 0, suppliers: 0 },
      indicators: {
        expense: { total: 0, paid: 0, toPay: 0, overdue: 0, dueToday: 0 },
        revenue: { total: 0, received: 0, toReceive: 0, overdue: 0, dueToday: 0 }
      }
    };
    list.forEach(d => {
      const c = d?.counts || {};
      result.counts.employees += Number(c.employees || 0);
      result.counts.serviceStations += Number(c.serviceStations || 0);
      result.counts.customers += Number(c.customers || 0);
      result.counts.suppliers += Number(c.suppliers || 0);
      const exp = d?.indicators?.expense || {};
      result.indicators.expense.total += Number(exp.total || 0);
      result.indicators.expense.paid += Number(exp.paid || 0);
      result.indicators.expense.toPay += Number(exp.toPay || 0);
      result.indicators.expense.overdue += Number(exp.overdue || 0);
      result.indicators.expense.dueToday += Number(exp.dueToday || 0);
      const rev = d?.indicators?.revenue || {};
      result.indicators.revenue.total += Number(rev.total || 0);
      result.indicators.revenue.received += Number(rev.received || 0);
      result.indicators.revenue.toReceive += Number(rev.toReceive || 0);
      result.indicators.revenue.overdue += Number(rev.overdue || 0);
      result.indicators.revenue.dueToday += Number(rev.dueToday || 0);
    });
    return result;
  }

  const loadDashboardData = React.useCallback(async (selectedCompanies, period) => {
    try {
      setLoading(true);

      const dp = getDateParams(period, period === 'custom' ? customPeriod : currentDate) || {};
      const params = {};
      if (dp.startDate) params.startDate = dp.startDate;
      if (dp.endDate) params.endDate = dp.endDate;

      const isAll = Array.isArray(selectedCompanies) ? selectedCompanies.includes('all') : selectedCompanies === 'all';
      if (isAll) {
        const dashboardData = await Api.getGeneralDashboard('all', params);
        setData(dashboardData);
        setLoading(false);
        return;
      }

      const ids = (Array.isArray(selectedCompanies) ? selectedCompanies : [selectedCompanies]).filter(Boolean);
      if (ids.length === 1) {
        const dashboardData = await Api.getGeneralDashboard(ids[0], params);
        setData(dashboardData);
        setLoading(false);
        return;
      }

      if (ids.length > 1) {
        const responses = await Promise.all(ids.map(id => Api.getGeneralDashboard(id, params)));
        const merged = mergeDashboards(responses);
        setData(merged);
        setLoading(false);
        return;
      }

      const fallbackData = await Api.getGeneralDashboard('all', params);
      setData(fallbackData);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar dashboard.');
      setLoading(false);
    }
  }, [currentDate, customPeriod]);

  useEffect(() => {
    loadDashboardData(filterCompanyIds, periodMode);
  }, [filterCompanyIds, periodMode, currentDate, loadDashboardData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading && !data) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="dashboard-loading">
          <Skeleton count={1} height={100} style={{ marginBottom: '2rem' }} />
          <div className="dashboard-grid-top" style={{ marginBottom: '2rem' }}>
            <Skeleton count={1} height={100} />
            <Skeleton count={1} height={100} />
            <Skeleton count={1} height={100} />
            <Skeleton count={1} height={100} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
             <Skeleton count={1} height={300} />
             <Skeleton count={1} height={300} />
          </div>
        </div>
      </PageComponent>
    );
  }

  if (error) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="error-container">{error}</div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1>Dashboard Geral</h1>
            <p className="subtitle" style={{marginTop: 0}}>Visão geral da sua empresa</p>
          </div>
          <div className="dashboard-header-filters">
            <div className="dashboard-period-filter">
              <DatePeriodFilter
                periodMode={periodMode}
                setPeriodMode={setPeriodMode}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                customRange={customPeriod}
                setCustomRange={setCustomPeriod}
                label="Período"
              />
            </div>
            <div className="dashboard-company-select">
              <MultiSelect
                id="filter-company-dashboard"
                value={filterCompanyIds}
                onChange={handleCompanyFilterChange}
                label={"Empresas"}
                options={[
                  { value: 'all', label: 'TODAS' },
                  ...companies.map(c => ({
                    value: String(c.id),
                    label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`
                  }))
                ]}
                placeholder="Selecione empresas"
                forceHeight={'46px'}
              />
            </div>
          </div>
        </div>

        {/* Top KPIs - Counts */}
        <div className="dashboard-grid-top">
          {hasPermission(user, 'management') && (
            <Card>
              <div className="kpi-card-content">
                <div className="kpi-icon-wrapper blue">
                  <FiUsers />
                </div>
                <div className="kpi-info">
                  <span className="kpi-value">{data?.counts?.employees || 0}</span>
                  <span className="kpi-label">Funcionários</span>
                </div>
              </div>
            </Card>
          )}

          {hasPermission(user, 'management') && (
            <Card>
              <div className="kpi-card-content">
                <div className="kpi-icon-wrapper green">
                  <FiMapPin />
                </div>
                <div className="kpi-info">
                  <span className="kpi-value">{data?.counts?.serviceStations || 0}</span>
                  <span className="kpi-label">Postos</span>
                </div>
              </div>
            </Card>
          )}

          {hasPermission(user, 'financial') && (
            <Card>
              <div className="kpi-card-content">
                <div className="kpi-icon-wrapper purple">
                  <FiBriefcase />
                </div>
                <div className="kpi-info">
                  <span className="kpi-value">{data?.counts?.customers || 0}</span>
                  <span className="kpi-label">Clientes</span>
                </div>
              </div>
            </Card>
          )}

          {hasPermission(user, 'financial') && (
            <Card>
              <div className="kpi-card-content">
                <div className="kpi-icon-wrapper orange">
                  <FiTruck />
                </div>
                <div className="kpi-info">
                  <span className="kpi-value">{data?.counts?.suppliers || 0}</span>
                  <span className="kpi-label">Fornecedores</span>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Financial Cards */}
        {hasPermission(user, 'financial') && (
          <div className="dashboard-grid-middle">
            {/* Accounts Payable */}
            <Card>
              <div className="financial-card-header">
                <div className="financial-title">
                  <FiArrowDownCircle size={20} color="#ef4444" />
                  <span>Contas a Pagar</span>
                </div>
              </div>
              
              <div className="financial-main-value red">
                {formatCurrency(data?.indicators?.expense?.total)}
              </div>
              <div className="financial-subtitle">
                Total de saídas no período
              </div>

              <div className="analytics-list">
                <div className="analytics-item">
                  <span className="analytics-label">Pago</span>
                  <span className="analytics-value" style={{ color: '#10b981' }}>
                    {formatCurrency(data?.indicators?.expense?.paid)}
                  </span>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">A Pagar</span>
                  <span className="analytics-value" style={{ color: '#ef4444' }}>
                    {formatCurrency((data?.indicators?.expense?.toPay || 0) + (data?.indicators?.expense?.overdue || 0) + (data?.indicators?.expense?.dueToday || 0))}
                  </span>
                </div>
              </div>
            </Card>

            {/* Accounts Receivable */}
            <Card>
              <div className="financial-card-header">
                <div className="financial-title">
                  <FiArrowUpCircle size={20} color="#10b981" />
                  <span>Contas a Receber</span>
                </div>
              </div>
              
              <div className="financial-main-value green">
                {formatCurrency(data?.indicators?.revenue?.total)}
              </div>
              <div className="financial-subtitle">
                Total de entradas no período
              </div>

              <div className="analytics-list">
                <div className="analytics-item">
                  <span className="analytics-label">Recebido</span>
                  <span className="analytics-value" style={{ color: '#10b981' }}>
                    {formatCurrency(data?.indicators?.revenue?.received)}
                  </span>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">A Receber</span>
                  <span className="analytics-value" style={{ color: '#ef4444' }}>
                    {formatCurrency((data?.indicators?.revenue?.toReceive || 0) + (data?.indicators?.revenue?.overdue || 0) + (data?.indicators?.revenue?.dueToday || 0))}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </PageComponent>
  );
}
